import React, { useEffect, useState } from 'react';

import FormatService from 'src/services/format.service';
import { InvestmentHoldingType } from 'src/types/investment-holding.model';
import AccountService from 'src/services/account.service';
import ManualAccountService from '../../../../services/manualAccount.service';
import {
    AccountsListViewModel,
    ManualAccountRowViewModel
} from 'src/types/view-models/listAccounts/accountsList.view.model';
import LiteralsService from 'src/services/literals/LiteralsService';

import ConnectedAccountRow from 'src/components/atomic/atoms/toggleAccountRow/ToggleAccountRow';
import Typography from 'src/components/typography/Typography';
import { TypographyVariants } from 'src/types/typography-variants.enum';

import colors from 'src/styles/colors.module.scss';
import styles from './accountsList.module.scss';
import { useAccountSyncStatuses } from 'src/hooks/queries/accounts';
import Selectors from 'src/utils/selectors';
import { getAccountNames } from 'src/utils/accounts.utils';
import Anchor from 'src/components/atomic/atoms/anchor/Anchor';

function ManualAccountRow({
    title,
    subtitle,
    amount,
    showBalance = true,
    onClick
}: ManualAccountRowViewModel): JSX.Element {
    return (
        <div className={styles.manualAccountRow}>
            <div>
                <Typography component="p" variant={TypographyVariants.SUBTEXT_BOLD}>
                    {onClick ? <Anchor onClick={onClick}>{title}</Anchor> : title}
                </Typography>
                <Typography component="p" variant={TypographyVariants.CAPTION}>
                    {subtitle}
                </Typography>
            </div>

            {showBalance && (
                <Typography component="p" variant={TypographyVariants.BODY}>
                    {amount}
                </Typography>
            )}
        </div>
    );
}

/** List of accounts with visibility toggle */
export default function AccountsList({
    heading,
    headingComponent,
    accountDrawerType,
    accounts = [],
    visibleAccounts = [],
    hiddenAccounts = [],
    nonDrawerTypeAccounts = [],
    investmentHoldings = [],
    debts = [],
    withTotal = false,
    showBalance = true,
    manualOnClick,
    toggleOnClick
}: AccountsListViewModel): JSX.Element {
    const [total, setTotal] = useState<string>();
    const { data: syncStatuses } = useAccountSyncStatuses();

    /** Update dict and total when accounts change */
    useEffect(() => {
        const newTotal: number = AccountService.getTotal(visibleAccounts, [], investmentHoldings, debts);
        setTotal(FormatService.format(newTotal));
    }, [visibleAccounts, hiddenAccounts, investmentHoldings, debts]);

    const AccountRows = (): JSX.Element => (
        <>
            {[
                ...accounts.map((e) => {
                    return { account: e };
                }),
                ...investmentHoldings.map((e) => {
                    return { investmentHolding: e };
                }),
                ...debts.map((e) => {
                    return { debt: e };
                })
            ]
                .sort((a, b) => {
                    const { name: aName, institutionName: aInstitutionName } = getAccountNames(a);
                    const { name: bName, institutionName: bInstitutionName } = getAccountNames(b);
                    return aInstitutionName === bInstitutionName
                        ? aName.localeCompare(bName)
                        : aInstitutionName.localeCompare(bInstitutionName);
                })
                .map((e, i) => {
                    if ('account' in e)
                        return (
                            <ConnectedAccountRow
                                key={`account-row-${e.account?.accountId}`}
                                title={AccountService.getDisplayName(e.account)}
                                subtitle={e.account?.institution?.name}
                                institution={e.account?.institution}
                                balance={FormatService.format(e.account?.current)}
                                hidden={e.account?.hidden}
                                id={e.account?.id}
                                accountId={e.account?.accountId}
                                onClick={async () => {
                                    // remove toggleOnClick when ListAccountsViewTemplate uses react query
                                    if (toggleOnClick) {
                                        await toggleOnClick(e.account, e.account?.hidden);
                                    }
                                }}
                                isSyncing={
                                    AccountService.isSyncingAccount(e.account?.accountId, syncStatuses) ||
                                    AccountService.hasSameInstitutionAsSyncingAccount(
                                        e.account?.institutionId,
                                        accounts,
                                        syncStatuses
                                    )
                                }
                                error={AccountService.getAccountSyncError(e.account?.accountId, syncStatuses)}
                                showBalance={showBalance}
                            />
                        );

                    if ('investmentHolding' in e)
                        return (
                            <ManualAccountRow
                                key={`account-row-${i}`}
                                title={e.investmentHolding.name}
                                subtitle={
                                    ManualAccountService.addAccountForms[
                                        ManualAccountService.getManualAccountType({
                                            investmentHolding: e.investmentHolding
                                        })
                                    ].label
                                }
                                amount={FormatService.format(
                                    e.investmentHolding.type === InvestmentHoldingType.Stock ||
                                        e.investmentHolding.type === InvestmentHoldingType.PrivateStock
                                        ? e.investmentHolding.price * e.investmentHolding.quantity
                                        : e.investmentHolding.amount
                                )}
                                onClick={() => manualOnClick({ investmentHolding: e.investmentHolding })}
                            />
                        );

                    return (
                        <ManualAccountRow
                            key={`account-row-${i}`}
                            title={e.debt.name}
                            subtitle={
                                ManualAccountService.addAccountForms[
                                    ManualAccountService.getManualAccountType({ debt: e.debt })
                                ].label
                            }
                            amount={FormatService.format(e.debt.amount)}
                            onClick={() => manualOnClick({ debt: e.debt })}
                        />
                    );
                })}
        </>
    );

    return (
        <div className={styles.accountsList} data-cy={Selectors.PlanAhead.ACCOUNTS_LIST}>
            <div>
                {headingComponent || (
                    <Typography variant={TypographyVariants.HEADING_XS} className={colors.textStrong}>
                        {heading}
                    </Typography>
                )}
                {withTotal && (
                    <Typography variant={TypographyVariants.HEADING_S} className={colors.textStrong}>
                        {total}
                    </Typography>
                )}
            </div>

            <AccountRows />

            <div className={styles.accounts}>
                {nonDrawerTypeAccounts?.length > 0 && (
                    <Typography
                        variant={TypographyVariants.CAPTION}
                        className={`${styles.extraAccounts} ${colors.textWeak}`}
                    >
                        {LiteralsService.getLiteral('Onboarding.extraAccounts', [
                            nonDrawerTypeAccounts.length,
                            accountDrawerType
                        ])}
                    </Typography>
                )}
            </div>
        </div>
    );
}
